import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import type { Database } from '@jac/shared/generated/db-types'
import { supabase } from '@/lib/supabase'
import {
  principalHome,
  principalKey,
  principalLabel,
  usePrincipals,
  useSession,
  type Principal,
} from '@/hooks/useSession'
import { useNewDocumentsCount } from '@/hooks/useNewDocumentsCount'
import { useUnreadNotificationsCount } from '@/hooks/useUnreadNotificationsCount'
import { useStickyNavigate, useStickySearch } from '@/lib/stickyParams'
import { PeriodProvider } from '@/contexts/PeriodContext'
import { pt } from '@/i18n/pt-PT'
import { cn } from '@/lib/cn'
import { RouteFallback } from '@/components/ui/RouteFallback'
import { XlsxPreviewModal } from '@/components/documents/XlsxPreviewModal'
import { ImagePreviewModal } from '@/components/documents/ImagePreviewModal'

// OperationsLayout — the platform shell for everything an authenticated user
// sees. Hosts both /operacoes/* and /admin/* (admin pages render their own
// tabbed sub-nav inside the <Outlet>). Visual: macOS-style rounded window
// container, single white surface with the sidebar on the left.

import { SAFTAI_DASHBOARD_URL } from '@/lib/saftai'

type Role = Database['public']['Enums']['user_role']

// SAFT-AI companion platform. The URL moved to lib/saftai.ts on 2026-09-29 so the
// client portal could reuse it — while it was private to this file, mirroring the
// link meant retyping a production hostname, which is part of why the portal went
// a month without one.
const SAFTAI_URL = SAFTAI_DASHBOARD_URL

type NavItemDef = {
  // Internal route (react-router NavLink). Mutually exclusive with `href`.
  to?: string
  // External URL — rendered as a plain <a> that navigates in the same tab.
  href?: string
  label: string
  end?: boolean
  icon: ReactNode
  count?: number
  // Tone for the count badge. 'novos' uses brand colours to flag fresh items
  // (e.g. docs received in the last 24h); default is the muted grey used for
  // neutral counts. Hidden when count is 0.
  countTone?: 'neutral' | 'novos'
  roles?: Role[]
  pathPrefix?: string // when set, item is "active" for any URL starting with this
}

function VisaoGeralIcon() {
  // 4-square grid: kept as the Analytics affordance (data dashboard).
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  )
}
function VisaoGeralOverviewIcon() {
  // Compass — "overview / orientation" affordance for the daily ops landing
  // page. Distinct from the Analytics dashboard grid.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <polygon points="15.5 8.5 11 11 8.5 15.5 13 13 15.5 8.5" />
    </svg>
  )
}
function CarteiraIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  )
}
function RiscoIcon() {
  // Triangle + exclamation mark — standard "warning / risk" pictogram.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}
function DocumentosIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h4" />
    </svg>
  )
}
function EFaturaIcon() {
  // e-Fatura — a document with a checkmark (fiscal record).
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
      <path d="M14 2v5h5M9 14l2 2 4-4" />
    </svg>
  )
}
function ExtratosIcon() {
  // Bank / statement — a columned building over a base line.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 21h18M4 10h16M12 3 4 7h16z" />
      <path d="M6 10v8M10 10v8M14 10v8M18 10v8" />
    </svg>
  )
}
function IvaIcon() {
  // Percent — VAT / IVA.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  )
}
function AdminIcon() {
  // Wrench — admin/configuration affordance.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.9 2.9-2.5-2.5 2.9-2.9z" />
    </svg>
  )
}
function SaftAiIcon() {
  // Sparkles — "AI" affordance for the SAFT-AI companion platform.
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
      <path d="M19 15l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" />
    </svg>
  )
}
function BellIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  )
}
function ChevronDown() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

const NAV_ITEMS: NavItemDef[] = [
  // #28 — Analytics (formerly "Visão Global"). end: true so the underlying
  // /operacoes index path also lights this entry as active.
  {
    to: '/operacoes/analytics',
    label: pt.operations.sidebar.analyticsOps,
    end: true,
    icon: <VisaoGeralIcon />,
  },
  // #27 — Visão Geral sits directly under Analytics in the sidebar order.
  {
    to: '/operacoes/visao-geral',
    label: pt.operations.sidebar.visaoGeral,
    icon: <VisaoGeralOverviewIcon />,
  },
  { to: '/operacoes/clientes', label: pt.operations.sidebar.meusClientes, icon: <CarteiraIcon /> },
  { to: '/operacoes/risco', label: pt.operations.sidebar.risco, icon: <RiscoIcon /> },
  {
    to: '/operacoes/documentos',
    label: pt.operations.sidebar.documentos,
    icon: <DocumentosIcon />,
  },
  { to: '/operacoes/extratos', label: pt.operations.sidebar.extratos, icon: <ExtratosIcon /> },
  { to: '/operacoes/efatura', label: pt.operations.sidebar.efatura, icon: <EFaturaIcon /> },
  { to: '/operacoes/iva', label: pt.operations.sidebar.iva, icon: <IvaIcon /> },
  // External companion platform — opens SAFT-AI in the same tab.
  { href: SAFTAI_URL, label: pt.operations.sidebar.saftAi, icon: <SaftAiIcon /> },
  // #96 — Revisões + Minhas Correções moved into the Notificações section as tabs
  // (see NotificacoesLayout); they are no longer standalone sidebar entries. The
  // maker-checker review queue is always on; Revisões stays team_lead/admin-only
  // (enforced in the tab gate here + server-side in the functions).
]

// Admin sits in its own block right above the foot separator, not inline with
// the main nav — that way the separator visually groups Admin with
// Notificações + user (the "tools / account" cluster) rather than with
// day-to-day operations links.
const ADMIN_ITEM: NavItemDef = {
  to: '/admin',
  label: pt.operations.sidebar.admin,
  icon: <AdminIcon />,
  roles: ['admin'],
  pathPrefix: '/admin',
}

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

export function OperationsLayout() {
  const session = useSession()
  const navigate = useStickyNavigate()
  const location = useLocation()
  const role = session.status === 'authenticated' ? session.consultant.role : null
  const fullName = session.status === 'authenticated' ? session.consultant.full_name : ''
  const roleLabel =
    session.status === 'authenticated'
      ? pt.admin.consultants.roleOptions[session.consultant.role]
      : ''

  async function signOut() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true }) // sticky params preserved by useStickyNavigate
  }

  const newDocsCount = useNewDocumentsCount()
  const unreadNotifsCount = useUnreadNotificationsCount()
  const visibleNav = useMemo(
    () =>
      NAV_ITEMS.filter((i) => !i.roles || (role && i.roles.includes(role))).map((item) =>
        item.to === '/operacoes/documentos'
          ? { ...item, count: newDocsCount, countTone: 'novos' as const }
          : item,
      ),
    [role, newDocsCount],
  )

  return (
    <PeriodProvider>
      {/* 288px = the DS --ds-sidebar-width (18rem), so the grid track matches the panel. */}
      <div className="grid h-screen grid-cols-[288px_minmax(0,1fr)] overflow-hidden bg-surface text-gray-900">
        {/* Sidebar — fixed full-height, scrolls independently if content overflows */}
        {/* SAFT-AI sidebar (2026-08-26 restyle): the DS's dark-navy panel via `platform-sidebar`.
            Tailwind `static h-screen` overrides the DS's position:fixed — this works because
            globals.css loads AFTER the DS in main.tsx (cascade order is load-bearing), keeping
            the panel inside the grid so the <main> scroll/sticky architecture is untouched. */}
        <aside className="platform-sidebar static h-screen w-full translate-x-0 gap-3 overflow-y-auto">
          <span className="platform-sidebar-brand-card">
            <span className="platform-sidebar-brand-mark">JA</span>
            <span className="platform-sidebar-brand-copy">
              <span className="platform-sidebar-brand-title">
                {pt.operations.sidebar.brandName}
              </span>
              <span className="platform-sidebar-brand-subtitle">
                {pt.operations.sidebar.brandSub}
              </span>
            </span>
          </span>

          <div className="flex flex-col gap-1 px-4">
            {visibleNav.map((item) => (
              <SidebarLink key={item.to ?? item.href} item={item} location={location} />
            ))}
          </div>

          {/* Admin sits right above the separator — grouped with the foot
                "tools / account" cluster, not with day-to-day nav. */}
          {role === 'admin' && (
            <div className="mt-auto flex flex-col gap-1 px-4">
              <SidebarLink item={ADMIN_ITEM} location={location} />
            </div>
          )}

          <div className={cn('platform-sidebar-footer', role !== 'admin' && 'mt-auto')}>
            <div className="mb-1">
              <SidebarLink
                item={{
                  to: '/operacoes/notificacoes',
                  label: pt.operations.sidebar.notifications,
                  icon: <BellIcon />,
                  // Unread count, same "novos" nudge treatment as Documentos. Realtime-backed,
                  // so it appears on a fresh push and clears when the inbox marks rows read.
                  count: unreadNotifsCount,
                  countTone: 'novos',
                }}
                location={location}
              />
            </div>
            <UserMenu fullName={fullName} roleLabel={roleLabel} onSignOut={signOut} />
          </div>
        </aside>

        {/* Main — only this column scrolls vertically */}
        <main className="h-screen min-w-0 overflow-y-auto bg-canvas px-8 py-6">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
        {/* In-app xlsx viewer — opened from any "Ver" on a spreadsheet doc. */}
        <XlsxPreviewModal />
        {/* In-app image viewer with zoom — opened from any "Ver" on an image doc. */}
        <ImagePreviewModal />
      </div>
    </PeriodProvider>
  )
}

// Single sidebar link component — shared between the main nav loop and the
// admin block. Hover/active state has flat-left, rounded-right corners so the
// pill reads as a "tab" pulled in from the sidebar's left edge.
function SidebarLink({
  item,
  location,
}: {
  item: NavItemDef
  location: ReturnType<typeof useLocation>
}) {
  const isPrefixActive = !!item.pathPrefix && location.pathname.startsWith(item.pathPrefix)
  const stickySearch = useStickySearch()

  // SAFT-AI nav items (DS `platform-sidebar-nav-item`, same idiom as PortalLayout's navCls).
  // External link (e.g. SAFT-AI): plain <a>, same tab, never "active".
  if (item.href) {
    return (
      <a href={item.href} className="platform-sidebar-nav-item">
        <span className="platform-sidebar-nav-icon">{item.icon}</span>
        <span className="platform-sidebar-nav-label">{item.label}</span>
      </a>
    )
  }

  return (
    <NavLink
      to={`${item.to}${stickySearch}`}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'platform-sidebar-nav-item',
          (isActive || isPrefixActive) && 'platform-sidebar-nav-item--active',
        )
      }
    >
      <span className="platform-sidebar-nav-icon">{item.icon}</span>
      <span className="platform-sidebar-nav-label flex-1">{item.label}</span>
      {item.count != null && item.count > 0 && (
        <span
          className={cn(
            // Count badges on the dark panel: light-on-dark, the DS chip blue for "novos".
            'ml-auto rounded-md px-1.5 py-px font-mono text-[11px] font-semibold tabular-nums',
            item.countTone === 'novos' ? 'bg-[#6D9EFF]/30 text-white' : 'bg-white/15 text-white/80',
          )}
          aria-label={
            item.countTone === 'novos'
              ? `${item.count} ${pt.operations.sidebar.novosBadge}`
              : undefined
          }
        >
          {item.count}
        </span>
      )}
    </NavLink>
  )
}

// Click the user row to open a small popover with account-related actions.
// Click-outside + Escape both close it. Currently the only action is sign-out;
// will add Settings / Conta when those land.
function UserMenu({
  fullName,
  roleLabel,
  onSignOut,
}: {
  fullName: string
  roleLabel: string
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const { principals, active, switchTo } = usePrincipals()
  const navigate = useNavigate()

  // Plain navigate (not the sticky one): a principal switch changes area/tenant, so carrying
  // the current URL's filters over would apply one context's params to another.
  async function handleSwitch(p: Principal) {
    if (await switchTo(p)) navigate(principalHome(p))
  }

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          // Dark-panel trigger (the popover itself stays a light card below).
          'flex w-full items-center gap-2.5 rounded-md px-1 py-1.5 text-left transition-colors',
          'hover:bg-white/10',
          open && 'bg-white/10',
        )}
      >
        <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-white text-[10px] font-bold text-brand-900">
          {initials(fullName) || 'JA'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-white">{fullName}</div>
          <div className="text-[11.5px] text-white/60">{roleLabel}</div>
        </div>
        <span className={cn('text-white/60 transition-transform', open && 'rotate-180')}>
          <ChevronDown />
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden rounded-lg bg-surface shadow-card ring-1 ring-DEFAULT"
        >
          {/* Only rendered when the person actually holds more than one principal, so the single-
              principal experience (everyone today) is unchanged. */}
          {principals.length > 1 && (
            <div className="border-b border-DEFAULT py-1">
              <p className="px-3 py-1 text-[10.5px] font-medium uppercase tracking-wide text-gray-500">
                {pt.auth.switchPrincipal}
              </p>
              {principals.map((p) => {
                const isActive = active !== null && principalKey(active) === principalKey(p)
                return (
                  <button
                    key={principalKey(p)}
                    type="button"
                    role="menuitem"
                    disabled={isActive}
                    onClick={() => {
                      setOpen(false)
                      void handleSwitch(p)
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px]',
                      isActive ? 'cursor-default text-gray-400' : 'text-gray-700 hover:bg-gray-100',
                    )}
                  >
                    <span className="min-w-0 truncate">{principalLabel(p)}</span>
                    {isActive ? (
                      <span className="shrink-0 text-[10.5px]">{pt.auth.principalActive}</span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void onSignOut()
            }}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-gray-700 hover:bg-gray-100"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {pt.auth.signOut}
          </button>
        </div>
      )}
    </div>
  )
}
